import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {motion, useReducedMotion} from "framer-motion";
import {accountVariants} from "../../utilities/framerVariants";
import toast from "react-hot-toast";
import Spinner from "../elements/Spinner/Spinner";
import styles from "../Account/Account.module.scss";
import {Link} from "react-router-dom";
import Icon from "../elements/Icon/Icon";
import Button from "../elements/Button/Button";
import envelope from "../../assets/svg/envelope.svg";
import clsx from "clsx";
import addressIcon from "../../assets/svg/address.svg";
import Info from "../elements/Info/Info";
import {fetchCustomer, updateCustomer} from "../../store/actions/customerActions";
import avatar from '../../assets/images/avatars/customer.png';
import {updateCustomer as sendUpdateCustomer} from "../../services/customer";
import * as ACTIONS from "../../store/actions/customerActions";
import {setErrors} from "../../store/actions/errorsActions";

const Customer = () => {
    document.title = 'Customer | Invoice App';
    const dispatch = useDispatch();
    const {customer, loading} = useSelector((state) => state.customer);
    const errors = useSelector((state) => state.errors.customer);
    const [formData, setFormData] = useState({});
    const [activeTab, setActiveTab] = useState("personalInfo");

    const shouldReduceMotion = useReducedMotion();
    const variant = (element) => {
        return shouldReduceMotion
            ? accountVariants.reduced
            : accountVariants[element];
    };

    const address = customer?.[0] ? customer[0].address : {
        street: "",
        city: "",
        zip: "",
        region: "",
        district: "",
    };

    const address_ukr = customer?.[0] ? customer[0].address_ukr : {
        street_ukr: "",
        city_ukr: "",
        region_ukr: "",
        district_ukr: "",
    }

    const handleChangePassword = () => {
        toast.error('This feature is not available yet');
    }

    const handleCustomerChange = (event, type) => {
        const name = event?.target?.name;
        const value = event?.target?.value;

        switch (type) {
            case "address":
                setFormData((prevData) => ({
                    ...prevData,
                    address: {
                        ...prevData.address,
                        [name]: value,
                    },
                }));
                break;
            case "address_ukr":
                setFormData((prevData) => ({
                    ...prevData,
                    address_ukr: {
                        ...prevData.address_ukr,
                        [name]: value,
                    },
                }));
                break;
            default:
                setFormData({...formData, [name]: value});
                break
        }
    };

    const handleUpdateCustomer = async (afterUpdateCallback) => {
        try {
            const response = await sendUpdateCustomer(formData);
            dispatch(ACTIONS.updateCustomer(response.data));

            if (typeof afterUpdateCallback === "function") {
                afterUpdateCallback();
            }
            toast.success('Customer updated successfully.');
        } catch (error) {
            console.error("Error updating customer:", error);
            dispatch(setErrors(error, "update_customer", ["Failed to update customer."]));
            toast.error("Failed to update customer.");
        }
    };

    useEffect(() => {
        dispatch(fetchCustomer());
    }, []);

    useEffect(() => {
        if (customer && customer[0]) {
            setFormData({
                ...customer[0],
                address: customer[0].address || {
                    street: "",
                    city: "",
                    zip: "",
                    region: "",
                    district: "",
                },
                address_ukr: customer[0].address_ukr || {
                    street_ukr: "",
                    city_ukr: "",
                    region_ukr: "",
                    district_ukr: "",
                },
            });
        } else {
            setFormData({
                address: {
                    street: "",
                    city: "",
                    zip: "",
                    region: "",
                    district: "",
                },
                address_ukr: {
                    street_ukr: "",
                    city_ukr: "",
                    region_ukr: "",
                    district_ukr: "",
                },
            });
        }
    }, [customer]);

    if (loading) {
        return <Spinner/>
    }

    const renderTabContent = () => {
        if (activeTab === "personalInfo") {
            return (
                <motion.div
                    className={styles.container}
                    variants={variant('form')}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                >
                    <legend className={styles.legend}>Personal Information / Персональна інформація</legend>

                    <div className={styles.columns}>
                        <div className={styles.column1}>
                            <div className={styles.inputContainer}>
                                <label htmlFor="name">Name</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={(event) => handleCustomerChange(event, 'name')}
                                />
                            </div>
                            <div className={styles.inputContainer}>
                                <label htmlFor="surname">Surname</label>
                                <input
                                    type="text"
                                    name="surname"
                                    value={formData.surname}
                                    onChange={(event) => handleCustomerChange(event, 'surname')}
                                />
                            </div>
                        </div>
                        <div className={styles.column2}>
                            <div className={styles.inputContainer}>
                                <label htmlFor="name_ukr">Ім'я</label>
                                <input
                                    type="text"
                                    name="name_ukr"
                                    value={formData.name_ukr}
                                    onChange={(event) => handleCustomerChange(event, 'name_ukr')}
                                />
                            </div>
                            <div className={styles.inputContainer}>
                                <label htmlFor="surname_ukr">Прізвище</label>
                                <input
                                    type="text"
                                    name="surname_ukr"
                                    value={formData.surname_ukr}
                                    onChange={(event) => handleCustomerChange(event, 'surname_ukr')}
                                />
                            </div>
                        </div>

                    </div>
                </motion.div>
            );
        }

        if (activeTab === "address") {
            return (
                <motion.div
                    className={styles.container}
                    variants={variant('form')}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                >
                    <div className={styles.info}>
                        <span>Full Address:</span>
                        <p className={styles.infoText}>
                            {address.region || address.city || address.district || address.street || address.zip
                                ? [address.region, address.city, address.district, address.street, address.zip]
                                    .filter(Boolean)
                                    .join(', ')
                                : 'Not available'}
                        </p>
                    </div>
                    <div className={styles.info}>
                        <span>Повна адреса:</span>
                        <p className={styles.infoText}>
                            {address_ukr.region_ukr || address_ukr.city_ukr || address_ukr.district_ukr || address_ukr.street_ukr || address.zip
                                ? [address_ukr.region_ukr, address_ukr.city_ukr, address_ukr.district_ukr, address_ukr.street_ukr, address.zip]
                                    .filter(Boolean)
                                    .join(', ')
                                : 'Not available'}
                        </p>
                    </div>
                    <div className={styles.columns}>
                        <div className={styles.column1}>
                            <div className={styles.inputContainer}>
                                <label htmlFor="region">Region</label>
                                <input
                                    type="text"
                                    name="region"
                                    value={formData.address?.region || ""}
                                    onChange={(event) => handleCustomerChange(event, 'address')}
                                />
                            </div>
                            <div className={styles.inputContainer}>
                                <label htmlFor="city">City</label>
                                <input
                                    type="text"
                                    name="city"
                                    value={formData.address?.city || ""}
                                    onChange={(event) => handleCustomerChange(event, 'address')}
                                />
                            </div>
                            <div className={styles.inputContainer}>
                                <label htmlFor="district">District</label>
                                <input
                                    type="text"
                                    name="district"
                                    value={formData.address?.district || ""}
                                    onChange={(event) => handleCustomerChange(event, 'address')}
                                />
                            </div>
                            <div className={styles.inputContainer}>
                                <label htmlFor="street">Street</label>
                                <input
                                    type="text"
                                    name="street"
                                    value={formData.address?.street || ""}
                                    onChange={(event) => handleCustomerChange(event, 'address')}
                                />
                            </div>
                        </div>

                        <div className={styles.column2}>
                            <div className={styles.inputContainer}>
                                <label htmlFor="region_ukr">Регіон</label>
                                <input
                                    type="text"
                                    name="region_ukr"
                                    value={formData.address_ukr?.region_ukr || ""}
                                    onChange={(event) => handleCustomerChange(event, 'address_ukr')}
                                />
                            </div>
                            <div className={styles.inputContainer}>
                                <label htmlFor="city_ukr">Місто</label>
                                <input
                                    type="text"
                                    name="city_ukr"
                                    value={formData.address_ukr?.city_ukr || ""}
                                    onChange={(event) => handleCustomerChange(event, 'address_ukr')}
                                />
                            </div>
                            <div className={styles.inputContainer}>
                                <label htmlFor="district_ukr">Район</label>
                                <input
                                    type="text"
                                    name="district_ukr"
                                    value={formData.address_ukr?.district_ukr || ""}
                                    onChange={(event) => handleCustomerChange(event, 'address_ukr')}
                                />
                            </div>
                            <div className={styles.inputContainer}>
                                <label htmlFor="street_ukr">Вулиця</label>
                                <input
                                    type="text"
                                    name="street_ukr"
                                    value={formData.address_ukr?.street_ukr || ""}
                                    onChange={(event) => handleCustomerChange(event, 'address_ukr')}
                                />
                            </div>
                        </div>
                    </div>

                    <div className={styles.inputContainer}>
                        <label htmlFor="postCode">Post Code / Поштовий індекс</label>
                        <input
                            type="text"
                            name="postCode"
                            value={formData.address?.zip || ""}
                            onChange={(event) => handleCustomerChange(event, 'address')}
                        />
                    </div>
                </motion.div>
            );
        }
    };

    if (customer[0])
        return (
            <div className={styles.account}>
                <div className={styles.banner}
                />
                <motion.div
                    className={styles.mainCard}
                    variants={variant('account')}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                >
                    <Link
                        className={styles.goBack}
                        to="/"
                    >
                        <Icon name={'arrow-left'} size={10}/>
                        Go back
                    </Link>
                    <img
                        src={avatar}
                        alt="Avatar"
                        className={styles.avatar}
                    />
                    <h2 className={styles.name}>
                        {customer[0].name} {customer[0].surname}
                    </h2>
                    <Button className={styles.changePassword} onClick={handleChangePassword}>Change Password</Button>
                </motion.div>
                <motion.div
                    className={styles.detailsCard}
                    variants={variant('account2')}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                >
                    <div className={styles.tabsContainer}>
                        <div className={styles.topBlock}>
                            <div className={styles.tabs}>
                                <button
                                    className={clsx(styles.tab, {[styles.active]: activeTab === "personalInfo"})}
                                    onClick={() => setActiveTab("personalInfo")}
                                >
                                    Personal Information
                                </button>
                                <button
                                    className={clsx(styles.tab, {[styles.active]: activeTab === "address"})}
                                    onClick={() => setActiveTab("address")}
                                >
                                    Address
                                </button>
                            </div>
                            <Info paragraphs={[
                                'Your personal information is used to generate invoices.',
                                'You can update your personal information at any time.',
                                'Please, make sure your information is correct and up to date.'
                            ]}
                            />
                        </div>

                        <motion.div
                            className={styles.detailsCard}
                            variants={variant('account2')}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                        >
                            <form className={styles.form}>
                                {renderTabContent()}
                            </form>
                        </motion.div>
                    </div>

                    <div className={styles.bottomBlock}>
                        <Button
                            className='primary'
                            onClick={() => handleUpdateCustomer(() => dispatch(fetchCustomer()))}
                            disabled={loading}
                        >
                            {loading ? 'Saving...' : 'Save'}
                        </Button>
                    </div>
                </motion.div>
            </div>
        );
};

export default Customer;
